import { Seo } from "@/components/Seo";
import { Reveal, SectionHead, Panel, Tag, FlowStack } from "@/components/System";
import { ARC_CONCEPTS, ARC_MAP, LINKS } from "@/data/content";
import { useState } from "react";

const ArcPage = () => {
    const [active, setActive] = useState("agents");
    const layer = ARC_MAP.find((l) => l.id === active);

    return (
        <div>
            <Seo path="/arc" />
            <section className="relative overflow-hidden px-4 pb-16 pt-36 sm:px-6">
                <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl">
                    <div className="vm-micro mb-6">AGENTVM://ARC_CONTEXT</div>
                    <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                        BUILT FOR THE
                        <br />
                        <span className="text-[#00D9FF] vm-glow">AGENTIC ECONOMY.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                        AgentVM is an official project within the Circle / Arc ecosystem. Arc provides the economic
                        infrastructure; AgentVM provides the verifiable runtime. Every technical statement below links
                        to an official source.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6" data-testid="arc-concepts">
                <SectionHead index="ARC.01" title="OFFICIALLY DOCUMENTED." sub="Concepts drawn from official Arc and Circle sources. Open the source behind any claim." />
                <div className="grid gap-4 md:grid-cols-2">
                    {ARC_CONCEPTS.map((c, i) => (
                        <Reveal key={c.term} delay={(i % 2) * 0.08}>
                            <div className="vm-panel-soft flex h-full flex-col p-5 transition-colors hover:border-[#00D9FF]/35" data-testid={`arc-concept-${i}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <h3 className="font-mono text-xs tracking-[0.16em] text-[#F5F9FF] sm:text-sm">{c.term}</h3>
                                    <Tag tone="live">OFFICIAL</Tag>
                                </div>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#B8C7D9]/85">{c.desc}</p>
                                <a href={c.source} target="_blank" rel="noopener noreferrer" data-testid={`arc-source-${i}`} className="vm-micro mt-4 text-[#00D9FF] hover:underline">
                                    SOURCE ↗
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="border-t border-[rgba(0,217,255,0.1)] bg-[#080F18]/40 py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <SectionHead index="ARC.02" title="ARC INFRASTRUCTURE MAP." sub="The economic stack beneath verifiable agents — from network to economic activity." />
                    <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr]">
                        <Reveal>
                            <FlowStack steps={ARC_MAP} activeId={active} onSelect={setActive} compact />
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Panel title={`ARC://LAYER/${layer.id.toUpperCase()}`} right="ECONOMIC OS" className="scanlines" testid="arc-inspector">
                                <div className="p-6 sm:p-8">
                                    <h3 className="font-mono text-xl tracking-[0.14em] text-[#F5F9FF] sm:text-2xl">{layer.name}</h3>
                                    <p className="mt-4 text-sm leading-relaxed text-[#B8C7D9] sm:text-base">{layer.desc}</p>
                                    <div className="vm-micro-dim mt-8 border-t border-[rgba(0,217,255,0.12)] pt-4">
                                        CONTEXT: ARC ECONOMIC LAYER — AGENTVM REMAINS THE RUNTIME BRAND
                                    </div>
                                </div>
                            </Panel>
                        </Reveal>
                    </div>
                    <Reveal delay={0.15}>
                        <div className="mt-10 flex flex-wrap gap-3">
                            {[
                                { label: "ARC.IO", url: LINKS.arc },
                                { label: "ARC DOCS", url: LINKS.arcDocs },
                                { label: "ARC PORTAL", url: LINKS.arcPortal },
                                { label: "CIRCLE AGENT STACK", url: LINKS.circleAgents },
                            ].map((l) => (
                                <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`arc-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="border border-[rgba(0,217,255,0.25)] px-4 py-2 font-mono text-[10px] tracking-[0.22em] text-[#B8C7D9] transition-colors hover:border-[#00D9FF]/70 hover:text-[#00D9FF]">
                                    {l.label} ↗
                                </a>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default ArcPage;
